/**
 * A ficha da leitura do caso (E139, GUS-142, ADR-0095). Espelha `apps/api/types/ficha_da_leitura.py`.
 *
 * - `GET /casos/:id/ficha-da-leitura?versao=`: a versão pedida (ou a mais recente) e a última.
 *   Caso sem ficha é 404 `FICHA_NOT_FOUND` — a leitura aparece depois do exame —, e o banco recebe
 *   403: a ficha é da equipe do escritório.
 * - "Ver na fonte" abre a página do arquivo recebido pela rota que já existe
 *   (`GET /documents/:id/pages/:n`), que confere a custódia antes de entregar a imagem.
 *
 * A ficha fica ao lado do exame: diz o que a leitura achou e onde, e o que não achou. A ausência
 * no documento só o perito afirma.
 */
import axios, { type AxiosInstance } from 'axios';

import { getDocumentPageUrl } from '@/lib/case_source_documents';

export type GrupoDaFicha = 'contrato' | 'pessoas_e_papeis' | 'atos_e_registros';

export type CampoDaFichaId =
  | 'numero_da_cedula'
  | 'data_de_emissao'
  | 'nascimento'
  | 'documento_de_identidade'
  | 'endereco'
  | 'finalidade'
  | 'valor_liberado'
  | 'saldo_devedor_pago'
  | 'forma_de_liberacao'
  | 'banco_da_liberacao'
  | 'agencia_da_liberacao'
  | 'conta_da_liberacao'
  | 'signatario_nome'
  | 'signatario_cpf'
  | 'signatario_email'
  | 'identificacao_na_plataforma'
  | 'correspondente'
  | 'instante_da_assinatura'
  | 'ip_do_signatario'
  | 'sessao_da_biometria'
  | 'codigo_declarado';

export type SituacaoDoCampo =
  | 'lido'
  | 'em_branco'
  | 'nao_localizado'
  | 'ilegivel'
  | 'nao_processado'
  | 'deduzido';

export type MotivoDaSituacao =
  | 'rotulo_e_valor_no_papel'
  | 'valor_no_papel'
  | 'valor_no_papel_ate_o_minuto'
  | 'valor_sem_trecho'
  | 'no_papel_sem_uso'
  | 'rotulo_sem_valor'
  | 'sem_documento_esperado'
  | 'nem_rotulo_nem_valor'
  | 'leitura_com_falha'
  | 'documento_sem_leitura'
  | 'pagina_sem_leitura'
  | 'operacao_nova'
  | 'marca_do_layout'
  | 'associacao_ambigua';

export type LeituraDoDocumento =
  | 'camada'
  | 'imagem'
  | 'leitura_pendente'
  | 'leitura_com_falha'
  | 'sem_texto'
  | 'fora_do_exame';

export type SituacaoDaPagina =
  | 'lida'
  | 'nada_lido'
  | 'por_ler'
  | 'leitura_com_falha'
  | 'em_branco'
  | 'imagem'
  | 'nao_processada'
  | 'sem_documento';

export type TextoDaPagina = 'camada' | 'imagem' | 'nenhum';

export interface FonteDoCampoData {
  documento_id: string;
  documento_nome: string;
  pagina: number | null;
  original_id: string;
  pagina_no_original: number | null;
  rotulo: string | null;
  literal: string | null;
  valor: string | null;
  trecho: string;
  origem_do_texto: 'camada' | 'imagem';
}

export interface CampoDaFichaData {
  campo: CampoDaFichaId;
  grupo: GrupoDaFicha;
  referente_id: string | null;
  referente_nome: string | null;
  valor: string | null;
  literal: string | null;
  situacao: SituacaoDoCampo;
  motivo: MotivoDaSituacao;
  fontes: FonteDoCampoData[];
  divergencias: FonteDoCampoData[];
  no_cadastro: string | null;
}

export interface DocumentoDaFichaData {
  documento_id: string;
  documento_nome: string;
  tipo: 'ccb' | 'comprovante' | 'laudo' | 'documento_de_identidade' | 'outro';
  leitura: LeituraDoDocumento;
  paginas: number;
  original_id: string;
  paginas_no_original: number[];
}

export interface PaginaDaFichaData {
  original_id: string;
  original_nome: string;
  pagina_no_original: number;
  documento_id: string | null;
  documento_nome: string | null;
  pagina: number | null;
  texto: TextoDaPagina;
  situacao: SituacaoDaPagina;
}

export interface FichaDaLeituraData {
  versao_do_catalogo: number;
  campos: CampoDaFichaData[];
  documentos: DocumentoDaFichaData[];
  paginas: PaginaDaFichaData[];
}

export interface FichaDaLeituraRecordData {
  id: string;
  case_id: string;
  versao: number;
  sha256: string;
  criada_em: string;
  ficha: FichaDaLeituraData;
}

export interface FichaDaLeituraOutData {
  ficha: FichaDaLeituraRecordData;
  ultima_versao: number;
}

export type MotivoDaFalhaDaFicha = 'sem_permissao' | 'indisponivel';

export class FichaDaLeituraError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDaFicha) {
    super(motivo);
    this.name = 'FichaDaLeituraError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const SEM_FICHA = 'FICHA_NOT_FOUND';

function semFicha(err: unknown): boolean {
  return (
    axios.isAxiosError(err) &&
    err.response?.status === HTTP_NOT_FOUND &&
    (err.response.data as { detail?: unknown } | undefined)?.detail === SEM_FICHA
  );
}

/** A ficha do caso; `null` quando o caso ainda não tem ficha (antes do primeiro exame). */
export async function lerFichaDaLeitura(
  client: AxiosInstance,
  caseId: string,
): Promise<FichaDaLeituraOutData | null> {
  try {
    const { data } = await client.get<FichaDaLeituraOutData>(`/casos/${caseId}/ficha-da-leitura`);
    return data;
  } catch (err) {
    if (semFicha(err)) return null;
    const proibido = axios.isAxiosError(err) && err.response?.status === HTTP_FORBIDDEN;
    throw new FichaDaLeituraError(proibido ? 'sem_permissao' : 'indisponivel');
  }
}

/** A imagem da página do arquivo recebido, como endereço local; quem chama o revoga. */
export async function lerImagemDaPagina(
  client: AxiosInstance,
  documentoId: string,
  pagina: number,
): Promise<string> {
  const { data } = await client.get<Blob>(getDocumentPageUrl(documentoId, pagina), {
    responseType: 'blob',
  });
  return URL.createObjectURL(data);
}

export const GRUPOS: readonly GrupoDaFicha[] = ['contrato', 'pessoas_e_papeis', 'atos_e_registros'];

export interface GrupoDeCampos {
  grupo: GrupoDaFicha;
  campos: CampoDaFichaData[];
}

/** Os campos de cada grupo, na ordem do catálogo; grupo sem campo não aparece. */
export function camposPorGrupo(ficha: FichaDaLeituraData): GrupoDeCampos[] {
  return GRUPOS.map((grupo) => ({
    grupo,
    campos: ficha.campos.filter((campo) => campo.grupo === grupo),
  })).filter((g) => g.campos.length > 0);
}

/** A página do arquivo recebido onde a ocorrência está; `null` sem página (o Word). */
export interface PaginaNaFonte {
  documentoId: string;
  pagina: number;
  titulo: string;
}

export function paginaDaFonte(fonte: FonteDoCampoData): PaginaNaFonte | null {
  if (fonte.pagina_no_original === null) return null;
  return {
    documentoId: fonte.original_id,
    pagina: fonte.pagina_no_original,
    titulo: fonte.documento_nome,
  };
}

export interface ArquivoRecebido {
  originalId: string;
  nome: string;
  paginas: PaginaDaFichaData[];
}

/** As páginas de cada arquivo recebido, na ordem da ficha. */
export function paginasPorArquivo(paginas: readonly PaginaDaFichaData[]): ArquivoRecebido[] {
  const arquivos = new Map<string, ArquivoRecebido>();
  for (const pagina of paginas) {
    const arquivo = arquivos.get(pagina.original_id) ?? {
      originalId: pagina.original_id,
      nome: pagina.original_nome,
      paginas: [],
    };
    arquivo.paginas.push(pagina);
    arquivos.set(pagina.original_id, arquivo);
  }
  return [...arquivos.values()];
}

/** Quantas páginas em cada situação, só as que aparecem, na ordem da lista fechada. */
export const SITUACOES_DA_PAGINA: readonly SituacaoDaPagina[] = [
  'lida',
  'por_ler',
  'nada_lido',
  'leitura_com_falha',
  'em_branco',
  'imagem',
  'nao_processada',
  'sem_documento',
];

export function contagemDasPaginas(
  paginas: readonly PaginaDaFichaData[],
): { situacao: SituacaoDaPagina; total: number }[] {
  return SITUACOES_DA_PAGINA.map((situacao) => ({
    situacao,
    total: paginas.filter((p) => p.situacao === situacao).length,
  })).filter((c) => c.total > 0);
}

/** Há página-imagem em leitura: a ficha será regravada, e a tela relê. */
export function temPaginaEmLeitura(ficha: FichaDaLeituraData): boolean {
  return ficha.paginas.some((p) => p.situacao === 'por_ler');
}

/** O campo que pede o olhar do perito: não lido no papel, impresso sem uso no exame ou
 * divergente entre as fontes. */
export function pedeAtencao(campo: CampoDaFichaData): boolean {
  return (
    campo.situacao !== 'lido' ||
    campo.motivo === 'no_papel_sem_uso' ||
    campo.divergencias.length > 0
  );
}
